import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { RoutingConfig } from '../types';

interface RoutingDetailDialogProps {
	row: RoutingConfig;
	open: boolean;
	onClose: () => void;
	onToggle: (id: number) => void;
}

export function RoutingDetailDialog({
	row,
	open,
	onClose,
	onToggle,
}: RoutingDetailDialogProps) {
	const isActive = row.status === 'Active';

	const fields = [
		['Strategy', row.type],
		['Subdomain', row.subdomain],
		['Description', row.desc],
		['Status', row.status],
		['Created', row.created],
	];

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{row.name}</DialogTitle>
					<p className="text-sm text-muted-foreground">
						{row.type} · {row.subdomain} · Created {row.created}
					</p>
				</DialogHeader>

				<div className="flex items-center justify-between rounded-lg border bg-muted/20 p-3">
					<div className="flex items-center gap-2">
						<div
							className={`h-2.5 w-2.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-muted-foreground'}`}
						/>
						<span className="text-sm font-medium">
							{isActive ? 'Active routing' : 'Inactive routing'}
						</span>
					</div>
					<Button variant="outline" size="sm" onClick={() => onToggle(row.id)}>
						{isActive ? 'Deactivate' : 'Activate'}
					</Button>
				</div>

				<div className="space-y-1">
					{fields.map(([key, value]) => (
						<div
							key={key}
							className="flex items-center border-b py-2.5 text-sm"
						>
							<span className="w-32 text-muted-foreground">{key}</span>
							<span>{value}</span>
						</div>
					))}
				</div>

				<div>
					<h4 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
						Connectors
					</h4>
					<div className="space-y-2">
						{row.connectors.map((c, i) => (
							<div
								key={i}
								className="flex items-center gap-3 rounded-lg border p-3"
							>
								<span className="flex h-6 w-6 items-center justify-center rounded bg-muted text-[10px] font-bold">
									{c.logo}
								</span>
								<div className="flex-1">
									<span className="font-medium">{c.name}</span>
									<span className="ml-2 text-xs text-muted-foreground">
										{c.label}
									</span>
								</div>
								{c.pos !== undefined && !c.err && (
									<Badge variant="outline">
										{i === 0 ? 'Primary' : `Fallback ${i}`}
									</Badge>
								)}
								{c.weight !== undefined && (
									<Badge variant="secondary">{c.weight}%</Badge>
								)}
								{c.cap && (
									<Badge variant="outline">
										Cap: {c.cap.toLocaleString()}/day
									</Badge>
								)}
								{c.role && <Badge variant="outline">Overflow</Badge>}
								{c.err && <Badge variant="destructive">Error</Badge>}
							</div>
						))}
					</div>
				</div>

				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
